// The musical rules of the planet. Everything here is a pure function of server time,
// so every listener on Earth derives the same key, mode and bar without extra messages.

import { subsolarPoint } from "./geo";

/** All rooms share one musical clock so that snapshots and rooms line up. */
export const EPOCH = 0;
export const STEPS = 16;
export const LANES = 8;
/** One sixteenth note. 150 ms per step is 100 BPM. */
export const STEP_MS = 150;
export const BAR_MS = STEP_MS * STEPS;
/** Every chapter the planet changes mode. 32 bars ≈ 77 seconds. */
export const CHAPTER_BARS = 32;
/** How long a placed note lives before the planet forgets it. */
export const DEFAULT_TTL_BARS = 64;

export const VOICES = ["glass", "pluck", "marimba", "kalimba", "koto", "flute", "celesta", "ghost"] as const;
export type VoiceName = (typeof VOICES)[number];

/** Each continent gets its own instrument; cf.continent uses these codes. */
export const CONTINENT_VOICE: Record<string, VoiceName> = {
  EU: "glass",
  NA: "pluck",
  SA: "marimba",
  AF: "kalimba",
  AS: "koto",
  OC: "flute",
  AN: "celesta",
};

export const VOICE_LABEL: Record<VoiceName, string> = {
  glass: "Glass bells",
  pluck: "Electric pluck",
  marimba: "Marimba",
  kalimba: "Kalimba",
  koto: "Koto",
  flute: "Breath flute",
  celesta: "Celesta",
  ghost: "Ghost choir",
};

/** Octave offsets so the ensemble spreads across the spectrum. */
export const VOICE_OCTAVE: Record<VoiceName, number> = {
  glass: 1,
  pluck: 0,
  marimba: 0,
  kalimba: 1,
  koto: 0,
  flute: 1,
  celesta: 2,
  ghost: 0,
};

export function voiceForContinent(continent: string | undefined | null): VoiceName {
  return (continent && CONTINENT_VOICE[continent.toUpperCase()]) || "pluck";
}

export const MODES = {
  lydian: [0, 2, 4, 6, 7, 9, 11],
  ionian: [0, 2, 4, 5, 7, 9, 11],
  "major pentatonic": [0, 2, 4, 7, 9],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  "minor pentatonic": [0, 3, 5, 7, 10],
  aeolian: [0, 2, 3, 5, 7, 8, 10],
} as const satisfies Record<string, readonly number[]>;
export type ModeName = keyof typeof MODES;
export const MODE_ORDER = Object.keys(MODES) as ModeName[];

export const NOTE_NAMES = ["C", "C♯", "D", "E♭", "E", "F", "F♯", "G", "A♭", "A", "B♭", "B"];

export interface Harmony {
  /** Pitch class of the tonic, 0 = C. */
  root: number;
  rootName: string;
  mode: ModeName;
  scale: readonly number[];
  chapter: number;
  /** Longitude of the subsolar point that picked the key. */
  sunLon: number;
  sunLat: number;
}

export function barIndex(serverMs: number, epoch: number): number {
  return Math.floor((serverMs - epoch) / BAR_MS);
}

export function stepIndex(serverMs: number, epoch: number): number {
  return Math.floor((serverMs - epoch) / STEP_MS);
}

export function barStartMs(bar: number, epoch: number): number {
  return epoch + bar * BAR_MS;
}

/**
 * The sun picks the key. The globe is cut into twelve 30° slices of longitude and
 * each slice is a step around the circle of fifths, so as the Earth turns the song
 * modulates to a neighbouring key roughly every two hours and every modulation is
 * the smoothest one possible (one accidental changes).
 */
export function rootForSunLongitude(lon: number): number {
  const slice = Math.floor((((lon + 180) % 360) + 360) % 360 / 30) % 12;
  return (slice * 7) % 12;
}

/**
 * Harmony for a bar. The key comes from the sun at the start of the bar; the mode
 * walks through MODE_ORDER one chapter at a time, bright to dark and back.
 */
export function harmonyForBar(bar: number, epoch: number): Harmony {
  const start = barStartMs(bar, epoch);
  const sun = subsolarPoint(start);
  const root = rootForSunLongitude(sun.lon);
  const chapter = Math.floor(bar / CHAPTER_BARS);
  const n = MODE_ORDER.length;
  // Ping-pong through the modes so we never jump from darkest to brightest.
  const period = 2 * (n - 1);
  const k = ((chapter % period) + period) % period;
  const mode = MODE_ORDER[k < n ? k : period - k]!;
  return {
    root,
    rootName: NOTE_NAMES[root]!,
    mode,
    scale: MODES[mode],
    chapter,
    sunLon: sun.lon,
    sunLat: sun.lat,
  };
}

/** MIDI pitch for a lane. Lane 0 is the lowest note, LANES-1 the highest. */
export function laneToMidi(lane: number, h: Pick<Harmony, "root" | "scale">, voice: VoiceName): number {
  const len = h.scale.length;
  const octave = Math.floor(lane / len);
  const degree = h.scale[lane % len]!;
  return 48 + h.root + 12 * (octave + VOICE_OCTAVE[voice]) + degree;
}

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function midiName(midi: number): string {
  return `${NOTE_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;
}

/** Seconds until the next step boundary, given server time. */
export function msUntilNextStep(serverMs: number, epoch: number): number {
  const into = (serverMs - epoch) % STEP_MS;
  return into === 0 ? 0 : STEP_MS - into;
}
